import {
	data as json,
	type HeadersFunction,
	type MetaFunction,
	useFetcher,
} from 'react-router'
import { Button } from '#app/components/button.tsx'
import { CloudflareEmailOff } from '#app/components/cloudflare-email-off.tsx'
import {
	ButtonGroup,
	ErrorPanel,
	Field,
} from '#app/components/form-elements.tsx'
import { Grid } from '#app/components/grid.tsx'
import { HeroSection } from '#app/components/sections/hero-section.tsx'
import { H2, Paragraph } from '#app/components/typography.tsx'
import { handleFormSubmission } from '#app/utils/actions.server.ts'
import { getEnv } from '#app/utils/env.server.ts'
import { sendEmail } from '#app/utils/send-email.server.ts'
import { getUser } from '#app/utils/session.server.ts'
import { useRootData } from '#app/utils/use-root-data.ts'
import { type KCDHandle } from '#app/types.ts'
import { type Route } from './+types/contact'

function getErrorForName(name: string | null) {
	if (!name) return `Name is required`
	if (name.length < 2) return `Name is too short`
	if (name.length > 80) return `Name is too long`
	return null
}

function getErrorForEmail(email: string | null) {
	if (!email) return `Email is required`
	if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return `Email is invalid`
	if (email.length > 200) return `Email is too long`
	return null
}

function getErrorForSubject(subject: string | null) {
	if (!subject) return `Subject is required`
	if (subject.length <= 5) return `Subject is too short`
	if (subject.length > 120) return `Subject is too long`
	return null
}

function getErrorForBody(body: string | null) {
	if (!body) return `Body is required`
	if (body.length <= 40) return `Body is too short`
	if (body.length > 1001) return `Body is too long`
	return null
}

type ActionData = {
	status: 'success' | 'error'
	fields: {
		name?: string | null
		email?: string | null
		subject?: string | null
		body?: string | null
	}
	errors: {
		generalError?: string
		name?: string | null
		email?: string | null
		subject?: string | null
		body?: string | null
	}
}

export async function action({ request }: Route.ActionArgs) {
	const user = await getUser(request)
	return handleFormSubmission<ActionData>({
		request,
		validators: {
			name: user ? () => null : getErrorForName,
			email: user ? () => null : getErrorForEmail,
			subject: getErrorForSubject,
			body: getErrorForBody,
		},
		handleFormValues: async (fields) => {
			const { subject, body } = fields
			const name = user?.firstName ?? fields.name?.trim() ?? 'Visitor'
			const email = user?.email ?? fields.email?.trim() ?? ''
			const noSpamMessage = '- Sent via the HamidX contact form'
			const env = getEnv()
			const inbox =
				env.CONTACT_INBOX?.trim() || 'hamidlabstools@gmail.com'
			const fromEmail =
				env.EMAIL_FROM?.trim() || 'notifications@hamidslab.com'
			const fromName = env.EMAIL_FROM_NAME?.trim() || 'HamidX'

			try {
				await sendEmail({
					from: `"${name} (via ${fromName} contact form)" <${fromEmail}>`,
					replyTo: `"${name}" <${email}>`,
					to: `"Hamidul Islam" <${inbox}>`,
					subject: `[HamidX] ${subject}`,
					text: `${body}\n\nFrom: ${name} <${email}>\n${noSpamMessage}`,
				})
			} catch (error) {
				console.error('Contact form email failed', error)
				const actionData: ActionData = {
					fields,
					status: 'error',
					errors: {
						generalError:
							'Sorry, the message could not be sent right now. Please try again or email me directly.',
					},
				}
				return json(actionData, { status: 500 })
			}

			const actionData: ActionData = { fields, status: 'success', errors: {} }
			return json(actionData)
		},
	})
}

export const headers: HeadersFunction = () => ({
	'Cache-Control': 'private, max-age=3600',
	Vary: 'Cookie',
})

export const handle: KCDHandle = {
	getSitemapEntries: () => [{ route: '/contact' }],
}

export async function loader({ request }: Route.LoaderArgs) {
	return {
		socialMetas: (
			await import('#app/og/page-meta.server.ts')
		).buildPageSocialMetasForRequest(request, {
			title: 'Contact Hamidul Islam',
			description: 'Send Hamidul Islam a personal email.',
			keywords: '',
			image: '/images/heroes/contact.jpg',
			imageAlt: 'Contact Hamidul Islam',
		}),
	}
}

export const meta: MetaFunction<typeof loader> = ({ data }) =>
	data?.socialMetas ?? []

export default function ContactRoute() {
	const contactFetcher = useFetcher<typeof action>()
	const { user } = useRootData()
	const actionData = contactFetcher.data as ActionData | undefined
	const isDone = contactFetcher.state === 'idle' && actionData != null
	const emailSuccessfullySent = isDone && actionData.status === 'success'

	return (
		<div>
			<HeroSection
				title="Send me an email."
				subtitle="Like in the old days."
				imageProps={{
					src: '/images/heroes/contact.png',
					alt: 'Envelope and message illustration',
					className: 'object-contain',
					style: { aspectRatio: '1' },
				}}
			/>
			<main>
				<contactFetcher.Form
					method="POST"
					noValidate
					aria-describedby="contact-form-error"
				>
					<Grid>
						<div className="col-span-full mb-12 lg:col-span-8 lg:col-start-3">
							<H2>Email me</H2>
							<Paragraph>
								{`
                  I do my best to respond. For collaboration, product engineering
                  work, or a quick intro, email works well - or schedule a call
                  if you'd rather talk live.
                `}
							</Paragraph>
							<div className="bg-secondary mt-6 rounded-lg p-4">
								<Paragraph>
									Prefer a calendar link?{' '}
									<a href="https://cal.com/hamidxcal" className="underline">
										Schedule a call
									</a>
									.
								</Paragraph>
							</div>
						</div>

						<div className="col-span-full lg:col-span-8 lg:col-start-3">
							{user ? (
								<>
									<Field
										name="name"
										label="Name"
										placeholder="Your name"
										disabled={true}
										defaultValue={user.firstName}
									/>
									<Field
										type="email"
										label="Email"
										placeholder="person.doe@example.com"
										disabled={true}
										defaultValue={user.email}
										name="email"
									/>
								</>
							) : (
								<>
									<Field
										name="name"
										label="Name"
										placeholder="Your name"
										defaultValue={actionData?.fields.name ?? ''}
										error={actionData?.errors.name}
									/>
									<Field
										type="email"
										label="Email"
										placeholder="you@example.com"
										name="email"
										defaultValue={actionData?.fields.email ?? ''}
										error={actionData?.errors.email}
									/>
								</>
							)}
							<Field
								name="subject"
								label="Subject"
								placeholder="No subject"
								defaultValue={actionData?.fields.subject ?? ''}
								error={actionData?.errors.subject}
							/>
							<Field
								name="body"
								label="Body"
								type="textarea"
								placeholder="A clear and concise message works wonders."
								rows={8}
								defaultValue={actionData?.fields.body ?? ''}
								error={actionData?.errors.body}
							/>
							{emailSuccessfullySent ? (
								`Hooray, email sent!`
							) : (
								<ButtonGroup>
									<Button
										type="submit"
										disabled={contactFetcher.state !== 'idle'}
									>
										Send message
									</Button>
									<Button variant="secondary" type="reset">
										Reset form
									</Button>
								</ButtonGroup>
							)}
							{actionData?.errors.generalError ? (
								<ErrorPanel id="contact-form-error">
									{actionData.errors.generalError}
								</ErrorPanel>
							) : null}
							{!user ? (
								<Paragraph className="mt-6">
									Or email me directly at{' '}
									<CloudflareEmailOff>
										<a
											className="underlined"
											href="mailto:contact@hamidxweb.com"
										>
											contact@hamidxweb.com
										</a>
									</CloudflareEmailOff>
									.
								</Paragraph>
							) : null}
						</div>
					</Grid>
				</contactFetcher.Form>
			</main>
		</div>
	)
}
